import React from 'react';
import Layout from '../components/Layout';
import AdminNavBar from '../components/AdminNavBar';

interface List {
  id: number;
  name: string;
  description?: string;
  type?: 'public' | 'private' | string;
  optin?: 'single' | 'double' | string;
  subscriber_count?: number;
  subscriber_statuses?: {
    [key: string]: number;
  };
}

interface ListFormValues {
  name: string;
  type: 'public' | 'private';
  optin: 'single' | 'double';
  description: string;
}

export default function ManageLists() {
  const [lists, setLists] = React.useState<List[]>([]);
  const [loading, setLoading] = React.useState<boolean>(true);
  const [error, setError] = React.useState<string | null>(null);

  const [editingList, setEditingList] = React.useState<List | null>(null);
  const [isCreating, setIsCreating] = React.useState<boolean>(false);
  const [formValues, setFormValues] = React.useState<ListFormValues>({
    name: '',
    type: 'public',
    optin: 'double',
    description: '',
  });
  const [saving, setSaving] = React.useState<boolean>(false);
  const [saveError, setSaveError] = React.useState<string | null>(null);
  const [deletingId, setDeletingId] = React.useState<number | null>(null);

  const baseUrl = process.env.REACT_APP_API_URL || '';

  // Load lists from backend/Listmonk
  React.useEffect(() => {
    async function loadLists() {
      try {
        setError(null);
        const res = await fetch(`${baseUrl}/api/listmonk/lists`);

        if (!res.ok) {
          const text = await res.text();
          console.error('Lists fetch failed:', res.status, text);
          throw new Error(`Failed to load lists (${res.status})`);
        }

        const json = await res.json();
        console.log('ManageLists JSON:', json);

        const listData: List[] = Array.isArray(json)
          ? json
          : (json.data || json.lists || json.results || []);

        setLists(listData || []);
      } catch (err: any) {
        console.error('Failed to load lists:', err);
        setError(err?.message || 'Failed to load lists');
      } finally {
        setLoading(false);
      }
    }

    loadLists();
  }, [baseUrl]);

  // ---------- helpers for create / edit ----------

  function openCreate() {
    setIsCreating(true);
    setEditingList(null);
    setSaveError(null);
    setFormValues({
      name: '',
      type: 'public',
      optin: 'double',
      description: '',
    });
  }

  function openEdit(list: List) {
    setIsCreating(false);
    setEditingList(list);
    setSaveError(null);
    setFormValues({
      name: list.name || '',
      type: (list.type as 'public' | 'private') || 'public',
      optin: (list.optin as 'single' | 'double') || 'double',
      description: list.description || '',
    });
  }

  function closeEditor() {
    setIsCreating(false);
    setEditingList(null);
    setSaveError(null);
  }

  async function handleSave() {
    setSaving(true);
    setSaveError(null);

    try {
      const payload = {
        name: formValues.name,
        type: formValues.type,
        optin: formValues.optin,
        description: formValues.description,
      };

      if (isCreating) {
        // CREATE list
        const res = await fetch(`${baseUrl}/api/listmonk/lists`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });

        if (!res.ok) {
          const text = await res.text();
          console.error('Create list error:', res.status, text);
          throw new Error(`Create failed: ${res.status}`);
        }

        const createdJson = await res.json();
        const created: List =
          (createdJson.list as List) ||
          (createdJson.data as List) ||
          (createdJson as List);

        setLists((prev) => [created, ...prev]);
      } else {
        // UPDATE list
        if (!editingList) return;

        const res = await fetch(
          `${baseUrl}/api/listmonk/lists/${editingList.id}`,
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(payload),
          }
        );

        if (!res.ok) {
          const text = await res.text();
          console.error('Update list error:', res.status, text);
          throw new Error(`Save failed: ${res.status}`);
        }

        const updatedJson = await res.json();
        const updated: List =
          (updatedJson.list as List) ||
          (updatedJson.data as List) ||
          (updatedJson as List);

        setLists((prev) =>
          prev.map((l) => (l.id === updated.id ? updated : l))
        );
      }

      closeEditor();
    } catch (err: any) {
      console.error('Failed to save list:', err);
      setSaveError(err?.message || 'Failed to save list');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(list: List) {
    const confirmed = window.confirm(
      `Delete list "${list.name}" (ID ${list.id})? This cannot be undone.`
    );
    if (!confirmed) return;

    setDeletingId(list.id);
    try {
      const res = await fetch(`${baseUrl}/api/listmonk/lists/${list.id}`, {
        method: 'DELETE',
      });

      if (!res.ok) {
        const text = await res.text();
        console.error('Delete list error:', res.status, text);
        throw new Error(`Delete failed: ${res.status}`);
      }

      setLists((prev) => prev.filter((l) => l.id !== list.id));

      if (editingList && editingList.id === list.id) {
        closeEditor();
      }
    } catch (err: any) {
      console.error('Failed to delete list:', err);
      // Optional: show toast or error message
    } finally {
      setDeletingId(null);
    }
  }

  return (
    <Layout>
      <AdminNavBar />

      <div className="pt-[110px] min-h-screen bg-fractal-pattern-3 bg-cover bg-fixed p-10 relative">
        <h1 className="text-4xl font-extrabold text-center text-gray-800 mb-10">
          Manage Lists
        </h1>

        <div className="max-w-5xl mx-auto bg-white/80 backdrop-blur-md shadow-xl rounded-xl p-8 border border-gray-200">
          {loading ? (
            <p className="text-center text-gray-600 text-lg">Loading lists…</p>
          ) : error ? (
            <div className="text-center text-sm text-red-600">{error}</div>
          ) : (
            <>
              {/* Summary + create button */}
              <div className="mb-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-gray-700 mb-2">
                    Overview
                  </h2>
                  <p className="text-gray-700">
                    Lists loaded:{' '}
                    <span className="font-semibold">{lists.length}</span>
                  </p>
                </div>

                <div className="flex gap-3">
                  <button
                    type="button"
                    className="px-4 py-2 rounded-md bg-green-600 text-white text-sm hover:bg-green-700"
                    onClick={openCreate}
                  >
                    + New list
                  </button>
                </div>
              </div>

              {/* Lists table */}
              <div className="mt-4">
                {lists.length === 0 ? (
                  <div className="border border-dashed border-gray-300 rounded-lg p-6 text-center text-sm text-gray-500 bg-gray-50">
                    No lists found from Listmonk.
                  </div>
                ) : (
                  <div className="overflow-x-auto border border-gray-200 rounded-xl shadow-sm">
                    <table className="min-w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr className="text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
                          <th className="px-4 py-3">Name</th>
                          <th className="px-4 py-3">Type</th>
                          <th className="px-4 py-3">Opt-in</th>
                          <th className="px-4 py-3">Subscribers</th>
                          <th className="px-4 py-3">Description</th>
                          <th className="px-4 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="bg-white">
                        {lists.map((list, idx) => (
                          <tr
                            key={list.id}
                            className={`border-t border-gray-100 transition-colors ${
                              idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/60'
                            } hover:bg-emerald-50/60`}
                          >
                            <td className="px-4 py-3 align-top">
                              <div className="font-medium text-gray-800">
                                {list.name}
                              </div>
                              <div className="text-[11px] text-gray-400">
                                ID #{list.id}
                              </div>
                            </td>

                            <td className="px-4 py-3 align-top">
                              {list.type === 'public' && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold bg-emerald-100 text-emerald-800">
                                  Public
                                </span>
                              )}
                              {list.type === 'private' && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold bg-gray-200 text-gray-700">
                                  Private
                                </span>
                              )}
                              {!list.type && (
                                <span className="text-xs text-gray-400">
                                  —
                                </span>
                              )}
                            </td>

                            <td className="px-4 py-3 align-top">
                              {list.optin === 'double' && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold bg-blue-100 text-blue-800">
                                  Double opt-in
                                </span>
                              )}
                              {list.optin === 'single' && (
                                <span className="inline-flex items-center px-2 py-1 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-800">
                                  Single opt-in
                                </span>
                              )}
                              {!list.optin && (
                                <span className="text-xs text-gray-400">
                                  —
                                </span>
                              )}
                            </td>

                            <td className="px-4 py-3 align-top">
                              <div className="font-semibold text-gray-800">
                                {list.subscriber_count ?? 0}
                              </div>
                              {list.subscriber_statuses && (
                                <div className="mt-1 text-[11px] text-gray-500 space-x-2">
                                  {Object.entries(
                                    list.subscriber_statuses
                                  ).map(([status, count]) => (
                                    <span key={status}>
                                      {status}: {count}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </td>

                            <td className="px-4 py-3 align-top max-w-xs">
                              <p className="text-xs text-gray-700 whitespace-pre-line">
                                {list.description || '—'}
                              </p>
                            </td>

                            <td className="px-4 py-3 align-top">
                              <div className="flex justify-end gap-2">
                                <button
                                  type="button"
                                  className="text-xs px-3 py-1 rounded-full border border-gray-300 text-gray-700 hover:bg-gray-100 transition"
                                  onClick={() => openEdit(list)}
                                >
                                  Edit
                                </button>
                                <button
                                  type="button"
                                  className="text-xs px-3 py-1 rounded-full border border-red-200 text-red-700 hover:bg-red-50 disabled:opacity-60 transition"
                                  onClick={() => handleDelete(list)}
                                  disabled={deletingId === list.id}
                                >
                                  {deletingId === list.id
                                    ? 'Deleting…'
                                    : 'Delete'}
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Edit / Create panel */}
              {(editingList || isCreating) && (
                <div className="mt-8 p-4 border border-gray-200 rounded-lg bg-white">
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">
                    {isCreating ? 'New List' : 'Edit List'}
                  </h3>

                  {saveError && (
                    <div className="mb-3 text-sm text-red-600">
                      {saveError}
                    </div>
                  )}

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Name
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={formValues.name}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            name: e.target.value,
                          }))
                        }
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Type
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={formValues.type}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            type: e.target.value as 'public' | 'private',
                          }))
                        }
                      >
                        <option value="public">Public</option>
                        <option value="private">Private</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Opt-in Mode
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                        value={formValues.optin}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            optin: e.target.value as 'single' | 'double',
                          }))
                        }
                      >
                        <option value="double">Double opt-in</option>
                        <option value="single">Single opt-in</option>
                      </select>
                    </div>

                    <div className="md:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm min-h-[80px]"
                        value={formValues.description}
                        onChange={(e) =>
                          setFormValues((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <button
                      type="button"
                      className="px-4 py-2 rounded-md bg-blue-600 text-white text-sm disabled:opacity-60"
                      onClick={handleSave}
                      disabled={saving}
                    >
                      {saving
                        ? isCreating
                          ? 'Creating…'
                          : 'Saving…'
                        : isCreating
                        ? 'Create list'
                        : 'Save changes'}
                    </button>
                    <button
                      type="button"
                      className="px-4 py-2 rounded-md border border-gray-300 text-sm"
                      onClick={closeEditor}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
